/**
 * Admin Portal API Client for V2 Backend Integration
 * Handles all API communications with the NestJS backend
 */

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    role: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface QueueStats {
  total: number;
  pending: number;
  assigned: number;
  processing: number;
  completed: number;
  rejected: number;
  averageProcessingTime: number;
  totalRevenue: number;
}

export interface AiRequest {
  id: number;
  customerId: string;
  siteId?: string;
  requestType: string;
  businessType: string;
  status: string;
  priority: string;
  requestData: any;
  adminId?: string;
  generatedContent?: any;
  processingNotes?: string;
  estimatedCost?: number;
  actualCost?: number;
  createdAt: Date;
  updatedAt: Date;
  assignedAt?: Date;
  startedAt?: Date;
  completedAt?: Date;
  // Relations
  customer?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  site?: {
    id: string;
    name: string;
  };
  wizardSession?: {
    id: string;
    siteName: string;
    businessType: string;
  };
}

export interface QueueResponse {
  requests: AiRequest[];
  total: number;
  page: number;
  totalPages: number;
}

export interface EmailSettings {
  provider: string;
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  fromEmail: string;
  fromName: string;
  enabled: boolean;
}

export interface EmailProvider {
  id: string;
  name: string;
  host: string;
  port: number;
  secure: boolean;
}

export interface Customer {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  companyName?: string;
  businessType?: string;
  phoneNumber?: string;
  tier: string;
  status: string;
  isActive?: boolean;
  sitesCount?: number;
  aiRequestsCount?: number;
  totalSpent?: string;
  lastActivityAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface CustomerSite {
  id: string;
  name: string;
  domain: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date; // Issue #96: Soft-delete timestamp
  templateConfigVersion?: string; // #172: Track B — functional/shared schema version
  designArtifactVersion?: string; // #172: Track A — aesthetic/per-site build version
}

export interface CustomersResponse {
  customers: Customer[];
  total: number;
  page: number;
  totalPages: number;
}

export interface OrganizationMember {
  id: string;
  customerId: string;
  userId: string;
  role: string;
  permissions: string[];
  invitedBy?: string;
  joinedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface SiteIntegration {
  id: string;
  siteId: string;
  integrationType: string;
  integrationName: string;
  config: any;
  status: string;
  lastSyncAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  site?: {
    id: string;
    name: string;
    domain: string;
  };
}

// Billing interfaces (Issue #77)
export interface CustomerSubscription {
  id: string;
  customerId: string;
  customerEmail: string;
  customerName: string;
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'inactive' | 'cancelled' | 'past_due' | 'trialing';
  billingCycle: 'monthly' | 'yearly';
  price: number;
  sitesLimit: number;
  storageLimitGb: number;
  bandwidthLimitGb: number;
  aiGenerationLimit: number;
  customDomainAllowed: boolean;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  currentPeriodStart?: Date;
  currentPeriodEnd?: Date;
  trialEnd?: Date;
  cancelledAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface UsageStats {
  sitesUsed: number;
  sitesLimit: number;
  storageUsedGb: number;
  storageLimitGb: number;
  bandwidthUsedGb: number;
  bandwidthLimitGb: number;
  aiGenerationsUsed: number;
  aiGenerationLimit: number;
  billingPeriod: string;
}

export interface StripeEvent {
  id: string;
  stripeEventId: string;
  eventType: string;
  customerId?: string;
  customerEmail?: string;
  stripeCustomerId?: string;
  processedAt?: Date;
  errorMessage?: string;
  payloadSummary: Record<string, any>;
  createdAt: Date;
}

export interface CustomerBillingDetail {
  customerId: string;
  email: string;
  name: string;
  subscription?: CustomerSubscription;
  usage: UsageStats;
  stripeCustomerId?: string;
  stripeAccountId?: string;
  lifetimeValue: number;
  recentEvents: StripeEvent[];
}

// Billing Dashboard interfaces (Issue #77)
export interface BillingDashboardStats {
  mrr: number;
  arr: number;
  revenueThisMonth: number;
  revenueThisYear: number;
  activeSubscriptions: number;
  cancelledThisMonth: number;
  churnRate: number;
  newSubscriptionsThisMonth: number;
  byPlan: {
    free: number;
    starter: number;
    professional: number;
    enterprise: number;
  };
  byStatus: {
    active: number;
    inactive: number;
    cancelled: number;
    past_due: number;
    trialing: number;
  };
  failedPaymentsThisMonth: number;
  refundsThisMonth: number;
  arpu: number;
}

export interface BillingChartData {
  period: string;
  revenue: number;
  newSubscriptions: number;
  cancellations: number;
}

export interface BillingChartResponse {
  data: BillingChartData[];
  range: '7d' | '30d' | '90d' | '12m';
}

export interface SubscriptionListResponse {
  data: CustomerSubscription[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface StripeEventListResponse {
  data: StripeEvent[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// #359 — activation funnel
export interface ActivationFunnelStep {
  step: string;
  label: string;
  count: number;
  conversionFromPrevious: number | null;
  conversionFromStart: number;
}

export interface ActivationStuckUser {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  lastEventAt: string;
  daysSinceLastEvent: number;
}

export interface ActivationWeeklyPoint {
  weekStart: string;
  counts: Record<string, number>;
}

class AdminAPIClient {
  private baseURL: string;

  constructor() {
    this.baseURL = this.getBaseURL();
  }

  private getBaseURL(): string {
    // Use environment variable if available (set by Ansible per environment)
    const envApiUrl = process.env.NEXT_PUBLIC_API_URL;
    if (envApiUrl) {
      return envApiUrl;
    }

    // Fallback to hostname detection for local development
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;

      // For lowebi domains (dev/test/prod), use relative URLs (nginx will proxy)
      if (hostname.includes('lowebi.com')) {
        return ''; // Empty string = relative URLs like /auth/login
      }

      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:7710'; // DEV backend port
      } else if (hostname.includes('162.55.213.90')) {
        return 'http://162.55.213.90:7710'; // DEV backend port (was 7610)
      }
    }

    return 'http://localhost:7710'; // Default fallback (DEV port)
  }

  private getAuthHeaders(): HeadersInit {
    const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : '';
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Network error' }));
      const errorMessage = error.message || `HTTP ${response.status}`;
      
      // Add more context for debugging
      console.error(`API Error: ${response.status} ${response.statusText} - ${errorMessage}`);
      console.error(`Request URL: ${response.url}`);
      
      // For 401 errors, also log token status
      if (response.status === 401) {
        const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;
        console.error(`Token present: ${!!token}, length: ${token?.length || 0}`);
      }
      
      throw new Error(errorMessage);
    }
    return response.json();
  }

  /**
   * Admin Authentication
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    return this.handleResponse<LoginResponse>(response);
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    return this.handleResponse<LoginResponse>(response);
  }

  /**
   * Queue Management
   */
  async getQueueStats(): Promise<QueueStats> {
    const response = await fetch(`${this.baseURL}/admin/queue/stats`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<QueueStats>(response);
  }

  async getQueue(
    page: number = 1,
    limit: number = 50,
    filters: {
      status?: string;
      requestType?: string;
      businessType?: string;
      priority?: string;
      fromDate?: string;
      toDate?: string;
    } = {}
  ): Promise<QueueResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
      ),
    });

    console.log(`🔍 Fetching queue: ${this.baseURL}/admin/queue?${params}`);
    const headers = this.getAuthHeaders();
    console.log('Headers:', Object.keys(headers));

    const response = await fetch(`${this.baseURL}/admin/queue?${params}`, {
      headers,
    });

    return this.handleResponse<QueueResponse>(response);
  }

  async getRequest(id: number): Promise<AiRequest> {
    const response = await fetch(`${this.baseURL}/admin/queue/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AiRequest>(response);
  }

  /**
   * Request Processing
   */
  async assignRequest(id: number): Promise<AiRequest> {
    const response = await fetch(`${this.baseURL}/admin/queue/${id}/assign`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AiRequest>(response);
  }

  async startProcessing(id: number): Promise<AiRequest> {
    const response = await fetch(`${this.baseURL}/admin/queue/${id}/start`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AiRequest>(response);
  }

  async completeRequest(id: number, data: { content: any }): Promise<AiRequest> {
    const response = await fetch(`${this.baseURL}/admin/queue/${id}/complete`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        generatedContent: data.content, // Already parsed JSON object
      }),
    });

    return this.handleResponse<AiRequest>(response);
  }

  async rejectRequest(id: number, reason: string): Promise<AiRequest> {
    const response = await fetch(`${this.baseURL}/admin/queue/${id}/reject`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });

    return this.handleResponse<AiRequest>(response);
  }

  /**
   * System Information
   */
  async getSystemHealth(): Promise<{ status: string; uptime: number; memory: any }> {
    const response = await fetch(`${this.baseURL}/admin/system/health`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse(response);
  }

  async getDashboardStats(): Promise<{
    customers: { total: number; active: number };
    sites: { total: number; active: number };
    aiQueue: QueueStats;
    systemHealth: { status: string };
  }> {
    const [queueStats, sitesResponse] = await Promise.all([
      this.getQueueStats(),
      fetch(`${this.baseURL}/admin/sites?page=1&limit=100`, {
        headers: this.getAuthHeaders(),
      }).then(r => r.ok ? r.json() : null).catch(() => null),
    ]);

    let totalSites = 0;
    let activeSites = 0;
    if (sitesResponse) {
      totalSites = sitesResponse.total ?? sitesResponse.sites?.length ?? 0;
      activeSites = (sitesResponse.sites || []).filter(
        (s: any) => s.status === 'deployed' || s.status === 'active'
      ).length;
    }

    return {
      customers: { total: 0, active: 0 },
      sites: { total: totalSites, active: activeSites },
      aiQueue: queueStats,
      systemHealth: { status: 'Healthy' },
    };
  }

  /**
   * Email Settings
   */
  async getEmailSettings(): Promise<EmailSettings> {
    const response = await fetch(`${this.baseURL}/admin/settings/email`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<EmailSettings>(response);
  }

  async updateEmailSettings(settings: Partial<EmailSettings>): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseURL}/admin/settings/email`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(settings),
    });
    return this.handleResponse(response);
  }

  async testEmailConnection(): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseURL}/admin/settings/email/test-connection`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async sendTestEmail(to: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseURL}/admin/settings/email/send-test`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ to }),
    });
    return this.handleResponse(response);
  }

  async getEmailProviders(): Promise<EmailProvider[]> {
    const response = await fetch(`${this.baseURL}/admin/settings/email/providers`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<EmailProvider[]>(response);
  }

  /**
   * Customer Management
   */
  async getCustomers(
    page: number = 1,
    limit: number = 50,
    filters: {
      search?: string;
      status?: string;
      tier?: string;
    } = {}
  ): Promise<CustomersResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
      ),
    });

    const response = await fetch(`${this.baseURL}/admin/customers?${params}`, {
      headers: this.getAuthHeaders(),
    });

    // Transform backend response (data, limit) to frontend format (customers, totalPages)
    const backendResponse = await this.handleResponse<{
      data: Customer[];
      total: number;
      page: number;
      limit: number;
    }>(response);

    return {
      customers: backendResponse.data,
      total: backendResponse.total,
      page: backendResponse.page,
      totalPages: Math.ceil(backendResponse.total / backendResponse.limit),
    };
  }

  async getCustomer(id: string): Promise<Customer> {
    const response = await fetch(`${this.baseURL}/admin/customers/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Customer>(response);
  }

  async updateCustomer(id: string, data: Partial<Customer>): Promise<Customer> {
    const response = await fetch(`${this.baseURL}/admin/customers/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<Customer>(response);
  }

  async getCustomerSites(id: string, includeDeleted: boolean = false): Promise<CustomerSite[]> {
    const params = includeDeleted ? '?includeDeleted=true' : '';
    const response = await fetch(`${this.baseURL}/admin/customers/${id}/sites${params}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<CustomerSite[]>(response);
  }

  /**
   * Site Management - Issue #96: Soft-delete, restore, purge
   */
  async deleteSite(siteId: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/admin/sites/${siteId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
  }

  async restoreSite(siteId: string): Promise<CustomerSite> {
    const response = await fetch(`${this.baseURL}/admin/sites/${siteId}/restore`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<CustomerSite>(response);
  }

  // Issue #96: Get single site by ID (for polling status during restore)
  async getSite(siteId: string): Promise<CustomerSite> {
    const response = await fetch(`${this.baseURL}/admin/sites/${siteId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<CustomerSite>(response);
  }

  async purgeSite(siteId: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/admin/sites/${siteId}/purge`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
    // 204 No Content - no JSON to parse
  }

  async getCustomerMembers(id: string): Promise<OrganizationMember[]> {
    const response = await fetch(`${this.baseURL}/admin/customers/${id}/members`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<OrganizationMember[]>(response);
  }

  async getCustomerIntegrations(id: string): Promise<SiteIntegration[]> {
    const response = await fetch(`${this.baseURL}/admin/customers/${id}/integrations`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<SiteIntegration[]>(response);
  }

  /**
   * Billing Management (Issue #77)
   */
  async getCustomerBilling(id: string): Promise<CustomerBillingDetail> {
    const response = await fetch(`${this.baseURL}/admin/billing/customers/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<CustomerBillingDetail>(response);
  }

  async updateSubscription(
    subscriptionId: string,
    data: Partial<{
      plan: string;
      status: string;
      sitesLimit: number;
      storageLimitGb: number;
      bandwidthLimitGb: number;
      aiGenerationLimit: number;
      customDomainAllowed: boolean;
    }>
  ): Promise<CustomerSubscription> {
    const response = await fetch(`${this.baseURL}/admin/billing/subscriptions/${subscriptionId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<CustomerSubscription>(response);
  }

  async cancelSubscription(
    subscriptionId: string,
    immediately: boolean = false,
    reason?: string
  ): Promise<CustomerSubscription> {
    const response = await fetch(`${this.baseURL}/admin/billing/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ immediately, reason }),
    });

    return this.handleResponse<CustomerSubscription>(response);
  }

  async issueRefund(
    customerId: string,
    amount: number,
    reason?: string
  ): Promise<{ success: boolean; refundId?: string; message: string }> {
    const response = await fetch(`${this.baseURL}/admin/billing/customers/${customerId}/refund`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ amount, reason }),
    });

    return this.handleResponse(response);
  }

  async addUsageCredit(
    customerId: string,
    type: string,
    amount: number,
    reason?: string
  ): Promise<void> {
    const response = await fetch(`${this.baseURL}/admin/billing/customers/${customerId}/credit`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ type, amount, reason }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
  }

  /**
   * Billing Dashboard (Issue #77)
   */
  async getBillingDashboardStats(): Promise<BillingDashboardStats> {
    const response = await fetch(`${this.baseURL}/admin/billing/dashboard`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<BillingDashboardStats>(response);
  }

  async getBillingChart(range: '7d' | '30d' | '90d' | '12m'): Promise<BillingChartResponse> {
    const response = await fetch(`${this.baseURL}/admin/billing/chart/${range}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<BillingChartResponse>(response);
  }

  async getSubscriptions(
    page: number = 1,
    limit: number = 20,
    filters: {
      search?: string;
      plan?: string;
      status?: string;
    } = {}
  ): Promise<SubscriptionListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
      ),
    });

    const response = await fetch(`${this.baseURL}/admin/billing/subscriptions?${params}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<SubscriptionListResponse>(response);
  }

  async getStripeEvents(
    page: number = 1,
    limit: number = 50,
    filters: {
      customerId?: string;
      eventType?: string;
      hasError?: boolean;
    } = {}
  ): Promise<StripeEventListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
      ),
    });

    const response = await fetch(`${this.baseURL}/admin/billing/events?${params}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<StripeEventListResponse>(response);
  }

  /**
   * Automation Management (Issue #106)
   */
  async getAllAutomations(
    page: number = 1,
    limit: number = 50,
    filters: {
      customerId?: string;
      siteId?: string;
      provider?: string;
      enabled?: boolean;
      search?: string;
    } = {}
  ): Promise<{
    data: any[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
      ),
    });

    const response = await fetch(`${this.baseURL}/admin/automations?${params}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse(response);
  }

  async getAutomationTemplates(): Promise<any[]> {
    const response = await fetch(`${this.baseURL}/admin/automations/templates`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse(response);
  }

  async createAutomationTemplate(data: {
    name: string;
    description?: string;
    provider: string;
    category: string;
    triggerType: string;
    // #270 — the backend DTO requires this as a real JSON object
    // (`@IsObject()`, no `@IsOptional()`); omitting it 400s with
    // "templateDefinition must be an object".
    templateDefinition: Record<string, unknown>;
    requiredConnections: string[];
    iconUrl?: string;
    isActive: boolean;
  }): Promise<any> {
    const response = await fetch(`${this.baseURL}/admin/automations/templates`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  async updateAutomationTemplate(id: string, data: {
    name?: string;
    description?: string;
    provider?: string;
    category?: string;
    triggerType?: string;
    templateDefinition?: Record<string, unknown>;
    requiredConnections?: string[];
    iconUrl?: string;
    isActive?: boolean;
  }): Promise<any> {
    const response = await fetch(`${this.baseURL}/admin/automations/templates/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  async deleteAutomationTemplate(id: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/admin/automations/templates/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
  }

  /**
   * #181 Phase 4: Fetch the 3rd-party tool version registry (pinned versions).
   */
  async getPlatformVersions(): Promise<{
    generatedAt: string;
    tools: Array<{
      id: string;
      name: string;
      category: string;
      pinnedVersion: string;
      managed: string;
      upstreamUrl: string;
      description: string;
    }>;
  }> {
    const response = await fetch(`${this.baseURL}/admin/platform/versions`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * #157: Fetch SSO credentials for an admin to open a customer automation
   * in ActivePieces or N8N. Returns instanceUrl + token + optional redirectPath,
   * mirroring the customer SSO response shape.
   */
  async getAutomationSso(automationId: string): Promise<{
    provider: 'ACTIVEPIECES' | 'N8N';
    instanceUrl: string;
    token: string;
    projectId?: string;
    redirectPath?: string;
    expiresAt: string;
  }> {
    const response = await fetch(
      `${this.baseURL}/admin/automations/${automationId}/sso`,
      { headers: this.getAuthHeaders() },
    );
    return this.handleResponse(response);
  }

  /**
   * #106 (Platform Health Dashboard): ActivePieces/N8N connectivity status
   * plus automation counts derived from stored integrations.
   */
  async getAutomationPlatformHealth(): Promise<{
    connectivity: Array<{
      provider: 'ACTIVEPIECES' | 'N8N';
      status: 'up' | 'down';
      responseTimeMs?: number;
      error?: string;
      checkedAt: string;
    }>;
    counts: {
      totalAutomations: number;
      activeAutomations: number;
      disabledAutomations: number;
      errorAutomations: number;
      byProvider: Record<string, { total: number; active: number }>;
    };
    checkedAt: string;
  }> {
    const response = await fetch(`${this.baseURL}/admin/automations/health`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * #106 (Platform Health Dashboard): the most recent automations currently
   * in an error sync state, across all customers.
   */
  async getAutomationErrorLog(limit: number = 50): Promise<{
    data: Array<{
      id: string;
      siteId: string | null;
      siteName: string | null;
      customerEmail: string;
      provider: string;
      displayName: string | null;
      errorMessage: string | null;
      syncStatus: string;
      lastSyncAt: string | null;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const response = await fetch(
      `${this.baseURL}/admin/automations/errors?limit=${limit}`,
      { headers: this.getAuthHeaders() },
    );
    return this.handleResponse(response);
  }

  /**
   * #359 — activation funnel
   */
  async getActivationFunnel(): Promise<{
    success: boolean;
    funnel: ActivationFunnelStep[];
  }> {
    const response = await fetch(`${this.baseURL}/admin/activation/funnel`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getActivationStuck(step: string): Promise<{
    success: boolean;
    step: string;
    users: ActivationStuckUser[];
  }> {
    const response = await fetch(
      `${this.baseURL}/admin/activation/stuck?step=${encodeURIComponent(step)}`,
      { headers: this.getAuthHeaders() },
    );
    return this.handleResponse(response);
  }

  async getActivationWeekly(weeks: number = 12): Promise<{
    success: boolean;
    weeks: ActivationWeeklyPoint[];
  }> {
    const response = await fetch(
      `${this.baseURL}/admin/activation/weekly?weeks=${weeks}`,
      { headers: this.getAuthHeaders() },
    );
    return this.handleResponse(response);
  }

  getActivationExportUrl(step: string): string {
    return `${this.baseURL}/admin/activation/export?step=${encodeURIComponent(step)}`;
  }

  async backfillActivationEvents(): Promise<{
    success: boolean;
    summary: {
      inserted: number;
      duplicateSkipped: number;
      skippedNoUser: number;
      perEventType: Record<string, number>;
    };
  }> {
    const response = await fetch(`${this.baseURL}/admin/activation/backfill`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }
}

export const apiClient = new AdminAPIClient();
export default apiClient;